// src/utils/sessionExpiredFlow.js
// SweetAlert (bruker bekrefter) → logout → /login
import { adaSwal } from './adaSwal';

export const AUTH_LOGOUT_REASON_KEY = 'authLogoutReason';

/** Hindrer parallelle kall (f.eks. idle-hook + 401-interceptor) som kan forstyrre dialogen. */
let sessionExpiredLogoutInFlight = null;

const sessionExpiredDialogDefaults = {
	allowOutsideClick: false,
	allowEscapeKey: false,
	showCloseButton: false,
};

const log = (...args) => {
	if (process.env.NODE_ENV === 'development') {
		console.log('[sessionExpiredFlow]', ...args);
	}
};

/** Lagre `logoutReason` fra API før `runSessionExpiredLogout` (f.eks. 401-body). */
export function stashLogoutReasonFromApiData(data) {
	const lr = data?.logoutReason;
	if (!lr) {
		return;
	}
	try {
		localStorage.setItem(AUTH_LOGOUT_REASON_KEY, lr);
	} catch {
		/* ignore */
	}
}

/**
 * Etter utløpt økt / mislykket refresh: vis dialog, deretter logout og hard redirect.
 * @param {() => Promise<void>} logout AuthContext.logout
 * @param {{ skipDialog?: boolean }} [options]
 *   `skipDialog` — brukeren har allerede fått passende Swal.
 */
export async function runSessionExpiredLogout(logout, options = {}) {
	if (sessionExpiredLogoutInFlight) {
		log('allerede i gang — venter på eksisterende flyt');
		return sessionExpiredLogoutInFlight;
	}

	const { skipDialog = false } = options;
	log('start — skipDialog:', skipDialog);

	const donePromise = (async () => {
		let logoutReason = null;
		try {
			logoutReason = localStorage.getItem(AUTH_LOGOUT_REASON_KEY);
			localStorage.removeItem(AUTH_LOGOUT_REASON_KEY);
		} catch {
			/* ignore */
		}

		if (!skipDialog) {
			if (logoutReason === 'idle_timeout') {
				await adaSwal({
					...sessionExpiredDialogDefaults,
					icon: 'info',
					title: 'Du er logget ut',
					html:
						'<p>Du har vært inaktiv for lenge uten at økten ble fornyet.</p>' +
						'<p>Eventuelle ulagrede endringer i skjema kan være tapt hvis de ikke rakk å autosaves.</p>',
					confirmButtonText: 'Til innlogging',
				});
			} else if (logoutReason === 'invalid_or_expired') {
				await adaSwal({
					...sessionExpiredDialogDefaults,
					icon: 'info',
					title: 'Økten er utløpt',
					text: 'Logg inn på nytt for å fortsette.',
					confirmButtonText: 'OK',
				});
			} else {
				await adaSwal({
					...sessionExpiredDialogDefaults,
					icon: 'warning',
					title: 'Kunne ikke fornye økten',
					html:
						'<p>Vi klarte ikke å fornye innloggingen din (nettverksfeil eller utløpt økt).</p>' +
						'<p>Trykk OK for å gå til innloggingssiden.</p>',
					confirmButtonText: 'OK',
				});
			}
		}

		await logout();
		window.location.href = '/login';
	})();

	sessionExpiredLogoutInFlight = donePromise;

	try {
		await donePromise;
	} finally {
		sessionExpiredLogoutInFlight = null;
	}
}
